<!--
// app/components/glossary/GlossaryCategoryAccordion.vue
-->
<script setup lang="ts">
import GlossaryConceptItem from "./GlossaryConceptItem.vue";

type Concept = {
  id: string;
  title: string;
  definition: string;
};

type Props = {
  categoryTitle: string;
  concepts: Concept[];
  highlightedConceptId?: string;
  defaultOpen?: boolean;
};

const props = defineProps<Props>();

const isOpen = ref(props.defaultOpen ?? false);

// Watch for highlighted concept in this category
watch(
  () => props.highlightedConceptId,
  newId => {
    if (newId && props.concepts.some(c => c.id === newId)) {
      isOpen.value = true;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="glossary-category border-b border-gray-700">
    <button
      type="button"
      class="flex items-center justify-between w-full py-4 px-2 text-left hover:bg-gray-800/50 transition-colors duration-200 rounded"
      @click="isOpen = !isOpen"
      :aria-expanded="isOpen"
    >
      <h3
        :class="[
          'font-semibold text-base transition-colors duration-200',
          isOpen ? 'text-mercato' : 'text-gray-200',
        ]"
      >
        {{ categoryTitle }}
      </h3>
      <svg
        :class="[
          'w-5 h-5 shrink-0 transition-transform duration-300',
          isOpen ? 'rotate-180 text-mercato' : 'rotate-0 text-gray-400',
        ]"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        ></path>
      </svg>
    </button>

    <div v-show="isOpen" class="pb-2">
      <glossary-concept-item
        v-for="concept in concepts"
        :key="concept.id"
        :id="concept.id"
        :title="concept.title"
        :definition="concept.definition"
        :is-highlighted="highlightedConceptId === concept.id"
      />
    </div>
  </div>
</template>

<style scoped></style>
